import { CompactText } from 'ui/CompactText';
import {
  EMPTY_STUDY_TIME,
  STUDY_DAYS,
  maskStudyTime,
  normalizeStudyTime,
  patchStudySlot,
  setStudyDaily,
  studyMode,
  studySlot,
  toggleStudySlot,
  type StudyTime,
} from 'utils/studyTime';
import styles from './StudyTimePicker.module.scss';

type Props = {
  value: StudyTime | null | undefined;
  onChange: (next: StudyTime) => void;
  error?: string;
  hint?: string;
  className?: string;
};

type TimeInputProps = {
  label: string;
  value: string;
  invalid: boolean;
  disabled?: boolean;
  ariaLabel?: string;
  onChange: (value: string) => void;
};

function TimeInput({ label, value, invalid, disabled, ariaLabel, onChange }: TimeInputProps) {
  return (
    <label className={styles.timeField}>
      <span className={styles.label}>{label}</span>
      <input
        className={styles.input}
        type='text'
        inputMode='numeric'
        autoComplete='off'
        placeholder='__:__'
        maxLength={5}
        pattern='([01][0-9]|2[0-3]):[0-5][0-9]'
        title='ЧЧ:ММ, часы 00-23, минуты 00-59'
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(maskStudyTime(e.target.value))}
      />
    </label>
  );
}

/**
 * «Когда удобно заниматься?» по Figma «Подбор - шаг 6» (21:14768) + режимы (p4):
 * тогл «Ежедневно»: вкл - одно время С-До на все дни, выкл - для каждого дня вкл/выкл + своё С-До.
 * Дни - кнопки Пн-Вс (48px, r12, выбранная #111), время - «С» / «До» (55px, r15, #eee), маска ЧЧ:ММ.
 */
export function StudyTimePicker({
  value,
  onChange,
  error,
  hint = 'Укажите время по вашему часовому поясу. Расписание можно уточнить с репетитором.',
  className = '',
}: Props) {
  const time = normalizeStudyTime(value || EMPTY_STUDY_TIME);
  const mode = studyMode(time);
  const invalid = Boolean(error);

  return (
    <div className={[styles.root, className].filter(Boolean).join(' ')} data-study-time data-mode={mode}>
      <label className={styles.dailySwitch} data-study-daily>
        <input
          type='checkbox'
          role='switch'
          className={styles.switchInput}
          checked={mode === 'daily'}
          aria-checked={mode === 'daily'}
          onChange={(e) => onChange(setStudyDaily(time, e.target.checked))}
        />
        <span className={styles.switchTrack} aria-hidden='true'>
          <span className={styles.switchThumb} />
        </span>
        <span className={styles.switchLabel}>Ежедневно</span>
      </label>

      {mode === 'per_day' ? (
        <div className={styles.perDay} role='group' aria-label='Время по дням недели'>
          {STUDY_DAYS.map((day) => {
            const slot = studySlot(time, day.value);
            const on = Boolean(slot);
            return (
              <div key={day.value} className={on ? styles.slotRowOn : styles.slotRow} data-slot-day={day.value}>
                <button
                  type='button'
                  className={on ? styles.dayOn : styles.day}
                  aria-pressed={on}
                  aria-label={day.full}
                  onClick={() => onChange(toggleStudySlot(time, day.value))}
                >
                  {day.label}
                </button>
                {on ? (
                  <>
                    <TimeInput
                      label='С'
                      ariaLabel={`${day.full}, с`}
                      value={slot!.time_from}
                      invalid={invalid}
                      onChange={(v) => onChange(patchStudySlot(time, day.value, { time_from: v }))}
                    />
                    <TimeInput
                      label='До'
                      ariaLabel={`${day.full}, до`}
                      value={slot!.time_to}
                      invalid={invalid}
                      onChange={(v) => onChange(patchStudySlot(time, day.value, { time_to: v }))}
                    />
                  </>
                ) : (
                  <span className={styles.slotOff}>Не занимаюсь</span>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className={styles.times}>
          <TimeInput
            label='С'
            value={time.time_from}
            invalid={invalid}
            onChange={(v) => onChange({ ...time, time_from: v })}
          />
          <TimeInput
            label='До'
            value={time.time_to}
            invalid={invalid}
            onChange={(v) => onChange({ ...time, time_to: v })}
          />
        </div>
      )}

      {error ? (
        <p className={styles.error} role='alert'>
          {error}
        </p>
      ) : null}
      {hint ? <CompactText className={styles.hint} tone={'info'}>{hint}</CompactText> : null}
    </div>
  );
}
